/**
 * ITER286 — Request Storm Detector (client-side telemetry).
 *
 * Enregistre chaque requête sortante axios et classifie les patterns
 * anormaux pour aider l'admin à diagnostiquer les régressions d'UX
 * (refetch loops, request storms, invalidations en cascade).
 *
 * Fonctionne 100% en front (pas de POST vers backend) — le widget
 * PerformanceAdminTab lit `window.__japap_storm_detector` en direct.
 *
 * Classifications :
 *  • realtime_legitimate  — 1 req par minute par path (bruit ambient)
 *  • polling_legitimate   — cadence stable > 3 s (metrics, dashboards)
 *  • dormant              — cadence stable mais document.hidden = true
 *  • storm                — > 5 req identiques (same URL + params) en 10 s
 *  • duplicate_burst      — 2+ req identiques simultanées (< 300 ms écart)
 *  • alternating_cycle    — même endpoint appelé alternativement avec
 *                            plusieurs setState → boucle React
 *  • same_endpoint_no_change — même URL + même params répétés sans
 *                                intervalle stable (rerender loop)
 *
 * Sortie exposée sur `window.__japap_storm_detector.getReport()`.
 */
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';
const WINDOW_MS = 10_000;
const MAX_HISTORY = 500;

const history = [];  // [{ at, method, url, params, hidden }]

function normalizeUrl(rawUrl) {
  if (!rawUrl) return '';
  let u = rawUrl;
  if (API && u.startsWith(API)) u = u.slice(API.length);
  return u.split('?')[0];
}

/**
 * ITER288 P2 — Signature de requête V2.
 * Une signature identifie DEUX requêtes comme identiques ssi :
 *   HTTP method + pathname + query params triés + body serialisé
 * Ainsi `?tab=blacklist` et `?tab=whitelist` sont considérées DIFFÉRENTES,
 * ce qui élimine les faux positifs "no-change loop" tout en gardant la
 * détection des vraies boucles (paramètres identiques répétés).
 */
function requestSignature(config) {
  try {
    const method = (config?.method || 'get').toLowerCase();
    const path = normalizeUrl(config?.url || '');
    const queryStr = ((config?.url || '').split('?')[1] || '');
    const sortedQuery = queryStr
      ? queryStr.split('&').filter(Boolean).sort().join('&')
      : '';
    const body = config?.data
      ? (typeof config.data === 'string' ? config.data : JSON.stringify(config.data)).slice(0, 300)
      : '';
    return `${method} ${path}?${sortedQuery}|${body}`;
  } catch { return ''; }
}

function serializeParams(config) {
  try {
    const q = (config?.url || '').split('?')[1] || '';
    const body = config?.data ? JSON.stringify(config.data).slice(0, 200) : '';
    return `${q}|${body}`;
  } catch { return ''; }
}

axios.interceptors.request.use((config) => {
  try {
    const entry = {
      at: Date.now(),
      method: (config.method || 'get').toLowerCase(),
      url: normalizeUrl(config.url),
      params: serializeParams(config),
      signature: requestSignature(config), // ITER288 P2 — V2 signature
      hidden: typeof document !== 'undefined' && document.visibilityState === 'hidden',
    };
    history.push(entry);
    if (history.length > MAX_HISTORY) history.splice(0, history.length - MAX_HISTORY);
  } catch { /* silent */ }
  return config;
});

function computeReport() {
  const now = Date.now();
  const recent = history.filter((e) => now - e.at < WINDOW_MS);
  // ITER288 P2 — Bucket par signature complète (method + path + query triée
  // + body), pas seulement par method+path. `?tab=blacklist` et
  // `?tab=whitelist` sont désormais 2 buckets distincts.
  const bySignature = new Map();
  for (const e of recent) {
    const key = e.signature || `${e.method} ${e.url}`;
    const bucket = bySignature.get(key) || {
      key, url: e.url, method: e.method, hits: [], hidden_hits: 0,
      params_variants: new Set(),
    };
    bucket.hits.push(e);
    if (e.hidden) bucket.hidden_hits += 1;
    bucket.params_variants.add(e.params);
    bySignature.set(key, bucket);
  }

  const items = [];
  for (const [, b] of bySignature) {
    const count = b.hits.length;
    // Comme on est groupé par signature complète, `uniqueParams` reflète
    // la variance body (rare). Utile pour détecter alternating_cycle.
    const uniqueParams = b.params_variants.size;
    // Détection burst (≥ 2 req identiques en < 300 ms)
    let duplicateBurst = false;
    const sorted = [...b.hits].sort((a, c) => a.at - c.at);
    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i].at - sorted[i-1].at < 300 && sorted[i].params === sorted[i-1].params) {
        duplicateBurst = true; break;
      }
    }
    // Détection cadence stable
    let stableCadence = null;
    if (sorted.length >= 3) {
      const gaps = sorted.slice(1).map((h, i) => h.at - sorted[i].at);
      const avg = gaps.reduce((s, x) => s + x, 0) / gaps.length;
      const stdev = Math.sqrt(gaps.reduce((s, x) => s + (x - avg) ** 2, 0) / gaps.length);
      if (stdev / (avg || 1) < 0.15 && avg > 500) stableCadence = Math.round(avg);
    }

    let classification;
    // ITER288 P2 — Nouvelles règles :
    //   • storm : > 5 hits sur MÊME signature (identique en tout)
    //   • duplicate_burst : ≥ 2 hits en < 300 ms (rerender simultané)
    //   • alternating_cycle : > 5 hits même URL mais paramètres qui varient
    //     (impossible ici car bucket=signature ; garde pour compat)
    //   • same_endpoint_no_change : ≥ 3 hits même signature sans cadence
    //     stable (VRAIE boucle React → setState → API)
    //   • dormant : polling stable en background
    //   • polling_legitimate : cadence stable ≥ 3s
    //   • realtime_legitimate : 1-2 hits (fetch initial + revisite occasionnelle)
    if (count > 5) classification = 'storm';
    else if (duplicateBurst) classification = 'duplicate_burst';
    else if (count >= 3 && !stableCadence) classification = 'same_endpoint_no_change';
    else if (stableCadence && b.hidden_hits === b.hits.length) classification = 'dormant';
    else if (stableCadence && stableCadence >= 3000) classification = 'polling_legitimate';
    else classification = 'realtime_legitimate';

    items.push({
      url: b.url, method: b.method,
      count, unique_params: uniqueParams,
      stable_cadence_ms: stableCadence,
      hidden_ratio: b.hits.length ? +(b.hidden_hits / b.hits.length).toFixed(2) : 0,
      classification,
    });
  }
  items.sort((a, b) => {
    // storm/burst/dormant en tête
    const order = { storm: 0, duplicate_burst: 1, alternating_cycle: 2,
                    same_endpoint_no_change: 3, dormant: 4,
                    polling_legitimate: 5, realtime_legitimate: 6 };
    return (order[a.classification] - order[b.classification]) || (b.count - a.count);
  });
  return {
    window_ms: WINDOW_MS,
    total_requests_10s: recent.length,
    unique_paths_10s: bySignature.size,
    generated_at: new Date().toISOString(),
    items,
  };
}

// Expose l'API sur window pour que le widget Admin la lise.
if (typeof window !== 'undefined') {
  window.__japap_storm_detector = {
    getReport: computeReport,
    getHistory: () => [...history],
    clear: () => { history.length = 0; },
    classifications: [
      'realtime_legitimate',
      'polling_legitimate',
      'dormant',
      'storm',
      'duplicate_burst',
      'alternating_cycle',
      'same_endpoint_no_change',
    ],
  };
}

export default computeReport;
